/**
 * PinUnlockScreen
 *
 * Door A (daily unlock). The app boots, finds a local backup in
 * localStorage, and routes here. User types PIN -> we decrypt the
 * local blob -> derive crypto keys -> hydrate store -> /inbox.
 *
 * After 5 failed attempts: wipe local cache and route to /welcome.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box, Cursor } from '../components/Frame';
import { useStore } from '../lib/store';
import { auth, signInAnonymously, callFn } from '../lib/firebase';
import {
  decryptMnemonic,
  loadLastSigil,
  clearLastSigil,
  loadLocalBackup,   // solo per migrazione one-shot dal vecchio formato
  clearLocalBackup,
  incLocalAttempts,
  resetLocalAttempts,
  getLocalAttempts,
  saveLastSigil,
  loadUnlockMode,
  saveUnlockMode,
  MAX_LOCAL_ATTEMPTS,
} from '../lib/pinCrypto';
import { mnemonicToSeed, isValid } from '../lib/mnemonic';
import { isNukeEnabled, executeNuke } from '../lib/nuke';
import {
  initSodium, keypairFromSeed, deriveUid,
  boxKeypairFromSeed, toBase64Url,
} from '../lib/crypto';
import { signInAsIdentity } from '../lib/authIdentity';
import { saveKeys } from '../lib/keys';
import { saveSession } from '../lib/storage';
import { publishMyKeysFromKeypairs } from '../lib/messaging';
import { ensureMyPrekeysPublished } from '../lib/fsMessaging';

export default function PinUnlockScreen() {
  const navigate = useNavigate();
  const { setIdentity, setBoxKeys, setFirebaseUser, setSigil } = useStore();

  const [sigil, setSigilLocal] = useState(null);
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState('pin'); // 'pin' (tastierino) | 'passphrase' (campo testo)
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [intro, setIntro] = useState(true); // splash logo iniziale (veloce, skippabile)
  // Promise dell'anon-auth avviata al mount: la riusiamo al submit invece di
  // avviarne una seconda in competizione (causa del "PIN due volte" su PC).
  const authPromiseRef = useRef(null);

  // Splash: mostra il logo ~650ms e poi rivela l'inserimento PIN. Veloce, così
  // non rallenta il flusso; durante questo tempo l'anon-auth si stabilizza.
  useEffect(() => {
    const t = setTimeout(() => setIntro(false), 1050);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // Bootstrap: leggi il sigil locale (formato nuovo). Se non c'e', migra
    // dal vecchio blob (legacy) estraendo il sigil e WIPANDO il blob.
    let s = loadLastSigil();
    if (!s) {
      const legacy = loadLocalBackup();
      if (legacy?.sigil) {
        s = legacy.sigil;
        saveLastSigil(s);
        clearLocalBackup(); // wipe del blob legacy — non lo usiamo piu'
      }
    }
    if (!s) {
      navigate('/welcome', { replace: true });
      return;
    }
    setSigilLocal(s);
    setMode(loadUnlockMode(s)); // default input: tastierino o passphrase
    setAttempts(getLocalAttempts());
    // Pre-stabilisci l'auth anonima ORA (non al submit): cosi' quando l'utente
    // tocca ▶ il token e' gia' pronto e fetchBackup non fallisce al 1° colpo.
    // Salviamo la PROMISE per aspettarla al submit (niente seconda sign-in).
    try {
      if (!auth().currentUser) {
        authPromiseRef.current = signInAnonymously(auth()).catch(() => null);
      }
    } catch {}
  }, [navigate]);

  function onDigit(d) {
    if (busy) return;
    // Updater FUNZIONALE: usa sempre il valore più recente di pin, così due tap
    // ravvicinati si accodano invece di sovrascriversi (niente input "saltati").
    setPin((prev) => (prev.length < 6 ? prev + d : prev));
  }
  function onBack() {
    if (busy) return;
    setPin((prev) => prev.slice(0, -1));
  }

  async function tryUnlock() {
    setErr(null);
    if (!sigil) return;
    if (mode === 'pin' && pin.length !== 6) {
      setErr('PIN deve essere 6 cifre');
      return;
    }
    if (mode === 'passphrase' && pin.length < 8) {
      setErr('la passphrase ha almeno 8 caratteri');
      return;
    }
    setBusy(true);
    try {
      // Door A passa SEMPRE per il server: fetchBackup ha rate-limit
      // 10/h, 30/24h per sigil enforzato server-side. Niente fallback offline:
      // se non c'e' rete, fallback non sicuro -> meglio bloccare e usare le
      // 24 parole. Il blob NON e' piu' in localStorage: niente brute force
      // offline possibile.
      // 1) Garantisci un utente anonimo con token FRESCO prima di chiamare il
      //    server. Su PC (nessuna sessione persistita) l'anon-auth avviata al
      //    mount puo' non essere ancora pronta al primo ▶: la ASPETTIAMO,
      //    invece di avviarne una seconda in competizione (era la causa del
      //    "PIN da digitare due volte": due sign-in concorrenti rendevano
      //    instabile il token alla prima fetchBackup → "no auth").
      try {
        if (!auth().currentUser && authPromiseRef.current) {
          await authPromiseRef.current;
        }
        if (!auth().currentUser) {
          await signInAnonymously(auth());
        }
        // Forza il minting del token: cosi' la callable lo allega davvero.
        await auth().currentUser?.getIdToken?.(true);
      } catch (e) {
        setErr('Serve connessione di rete per sbloccare. Se sei offline, riprova quando hai segnale, oppure usa le 24 parole.');
        return;
      }

      const sigilLower = (sigil || '').toLowerCase();

      // 2) fetchBackup con retry robusto: il primo colpo dopo il boot puo'
      //    tornare "no auth" (token non ancora propagato) o fallire per
      //    cold-start della function. Riproviamo fino a 3 volte con backoff
      //    PRIMA di mostrare qualunque errore → l'utente non ridigita mai il PIN.
      async function fetchBackupOnce() {
        const res = await callFn('fetchBackup')({ sigilLower });
        return res?.data || {};
      }
      let d = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, 500 * attempt));
          try { await auth().currentUser?.getIdToken?.(true); } catch {}
        }
        try {
          d = await fetchBackupOnce();
        } catch {
          d = { ok: false, error: 'transport' };
        }
        if (d.ok) break;            // successo (con o senza backup trovato)
        if (d.rateLimited) break;   // rate-limit: esito definitivo, non riprovare
        // altrimenti (no auth / transport) → riprova
      }
      if (!d || (!d.ok && !d.rateLimited)) {
        // Dopo 3 tentativi ancora ko: NON svuotiamo il PIN, basta ritoccare ▶.
        setErr('Connessione non ancora pronta — ritocca ▶ (il PIN è già inserito).');
        return;
      }

      let useBundle = null;
      if (d.rateLimited) {
        setErr(`Troppi tentativi. Riprova tra ${d.retryAfterMinutes || 60} minuti, oppure usa le 24 parole.`);
        setPin('');
        return;
      }
      if (!d.ok) {
        setErr(`Errore server: ${d.error || 'sconosciuto'}`);
        return;
      }
      if (!d.found) {
        setErr('Backup non presente sul server. Recupera con le 24 parole.');
        return;
      }
      useBundle = { blob: d.blob, salt: d.salt, nonce: d.nonce, argon2: d.argon2 || null };

      const mnemonic = await decryptMnemonic({
        pin,
        sigilLower,
        blob: useBundle.blob,
        salt: useBundle.salt,
        nonce: useBundle.nonce,
        argon2: useBundle.argon2,
      });

      if (!mnemonic || !isValid(mnemonic)) {
        // PIN sbagliato: il server ha gia' contato il tentativo nel rate-limit.
        const newAttempts = incLocalAttempts();
        setAttempts(newAttempts);
        setPin('');
        // NUKE 5-PIN: se abilitato e raggiunto il limite, distruggi tutto.
        if (isNukeEnabled() && newAttempts >= MAX_LOCAL_ATTEMPTS) {
          setErr('…');
          try { await executeNuke(sigil); } catch {}
          // Ritorno "pulito": sembra una prima installazione.
          window.location.replace('/welcome');
          return;
        }
        setErr(`${mode === 'passphrase' ? 'Passphrase sbagliata' : 'PIN sbagliato'}. (Il server limita i tentativi: dopo 10/h o 30/24h dovrai aspettare o usare le 24 parole.)`);
        return;
      }

      // Mnemonic decrypted — derive crypto material
      await initSodium();
      const seed = mnemonicToSeed(mnemonic);
      const signKp = await keypairFromSeed(seed);
      const boxKp = await boxKeypairFromSeed(seed);
      const cryptoUid = await deriveUid(signKp.publicKey);

      // Auth a token custom: l'anon effimero garantito sopra è servito solo per
      // fetchBackup (serve auth PRIMA di avere le chiavi). Ora passiamo alla
      // sessione DELL'IDENTITÀ (uid = deriveUid(pubKeySign) = cryptoUid), mai
      // condivisa con altre identità sullo stesso device → scollegabili.
      await signInAsIdentity({ signPub: signKp.publicKey, signSec: signKp.secretKey });
      const fbUser = auth().currentUser;

      // Hydrate store
      setIdentity({
        publicKey: signKp.publicKey,
        secretKey: signKp.secretKey,
        uid: cryptoUid,
        mnemonic,
      });
      setBoxKeys?.({ boxPub: boxKp.publicKey, boxSec: boxKp.secretKey });
      setFirebaseUser(fbUser);
      setSigil(sigil);

      resetLocalAttempts();
      saveUnlockMode(sigil, mode); // ricorda il segreto che ha funzionato (default al prossimo sblocco)

      // Vai SUBITO all'inbox: lo store è già idratato, l'inbox può renderizzare.
      // Le operazioni lente (rete + IDB) NON devono bloccare questa transizione.
      navigate('/inbox', { replace: true });

      // In BACKGROUND (non bloccano la comparsa dell'inbox): lega l'uid al sigil
      // (multi-device + migrazione da anon legacy → aggiunge questo device a
      // sigils/{sigil}.devices se la pubKeySign combacia; idempotente), ripubblica
      // le chiavi pubbliche e persiste le chiavi su IDB. Errori non bloccanti.
      toBase64Url(signKp.publicKey)
        .then((pubKeySignB64) => callFn('registerSigil')({ sigil, pubKeySign: pubKeySignB64 }))
        .catch((e) => console.warn('registerSigil (unlock)', e));
      publishMyKeysFromKeypairs(signKp.publicKey, boxKp.publicKey)
        .catch((e) => console.warn('publishKeys (unlock)', e));
      // Forward Secrecy: pubblica le prekey se questo device non ne ha.
      ensureMyPrekeysPublished({
        signKeypair: { pub: signKp.publicKey, priv: signKp.secretKey },
        sigil,
      }).catch((e) => console.warn('prekeys (unlock)', e));
      saveKeys({
        mnemonic,
        uid: cryptoUid,
        sigil,
        signPub: signKp.publicKey,
        signSec: signKp.secretKey,
        boxPub: boxKp.publicKey,
        boxSec: boxKp.secretKey,
        securityLevel: 1, // unlocked from PIN
      }).catch((e) => console.warn('saveKeys', e));
      try {
        saveSession({ mnemonic, uid: cryptoUid, sigil });
      } catch (e) { console.warn('saveSession', e); }
    } catch (e) {
      console.error('PIN unlock failed', e);
      setErr(e?.message || 'errore sblocco');
    } finally {
      setBusy(false);
    }
  }

  const dots = (val, len = 6) =>
    Array.from({ length: len }).map((_, i) => i < val.length ? '●' : '○').join(' ');

  if (!sigil) {
    return null; // redirecting
  }

  return (
    <Frame statusLeft="PIN_UNLOCK" statusRight="5G">
      <Header title={`// @${sigil}`} />

      <p className="text-[15px] text-ink-dim mb-2">
        {mode === 'passphrase' ? 'Inserisci la passphrase per sbloccare.' : 'Digita il PIN per sbloccare.'}
      </p>

      {mode === 'pin' ? (
        <>
          <div className="mb-2">
            <div className="text-ink text-glow text-[24px] tracking-[0.3em] whitespace-nowrap font-mono py-2.5 px-3 border border-ink-dim/50 rounded-xl bg-ink/[0.02] text-center">
              {dots(pin)}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1.5 mt-1.5">
            {[1,2,3,4,5,6,7,8,9].map((n) => (
              <button
                key={n}
                onPointerDown={() => onDigit(String(n))}
                disabled={busy}
                className="rounded-xl border border-ink-dim/50 bg-ink/[0.02] text-ink py-2.5 text-[22px] font-mono hover:bg-ink/[0.08] active:bg-ink/[0.12] transition-colors disabled:opacity-30 touch-manipulation select-none"
              >
                {n}
              </button>
            ))}
            <button
              onPointerDown={onBack}
              disabled={busy}
              className="rounded-xl border border-ink-dim/50 bg-ink/[0.02] text-amber py-2.5 text-[18px] font-mono hover:bg-amber/10 active:bg-amber/20 transition-colors disabled:opacity-30 touch-manipulation select-none"
            >
              ◀
            </button>
            <button
              onPointerDown={() => onDigit('0')}
              disabled={busy}
              className="rounded-xl border border-ink-dim/50 bg-ink/[0.02] text-ink py-2.5 text-[22px] font-mono hover:bg-ink/[0.08] active:bg-ink/[0.12] transition-colors disabled:opacity-30 touch-manipulation select-none"
            >
              0
            </button>
            <button
              onClick={tryUnlock}
              disabled={busy || pin.length !== 6}
              className="rounded-xl border border-ink bg-ink/[0.10] text-ink text-glow py-2.5 text-[18px] font-mono hover:bg-ink/20 active:bg-ink/30 transition-colors disabled:opacity-30 touch-manipulation select-none"
            >
              ▶
            </button>
          </div>
        </>
      ) : (
        <div className="mb-2">
          <input
            type={showPass ? 'text' : 'password'}
            autoComplete="current-password"
            value={pin}
            onChange={(e) => { if (!busy) setPin(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') tryUnlock(); }}
            disabled={busy}
            autoFocus
            className="w-full bg-black border border-ink-dim text-ink font-mono text-[18px] px-3 py-3 rounded-xl focus:border-ink focus:outline-none"
            placeholder="passphrase"
          />
          <div className="flex items-center justify-between mt-2.5">
            <button
              type="button"
              onClick={() => setShowPass((v) => !v)}
              className="text-[13px] text-ink-dim hover:text-ink"
            >
              {showPass ? '🙈 nascondi' : '👁 mostra'}
            </button>
            <button
              onClick={tryUnlock}
              disabled={busy || pin.length < 8}
              className="rounded-xl border border-ink bg-ink/[0.10] text-ink text-glow px-6 py-2.5 text-[16px] font-mono hover:bg-ink/20 active:bg-ink/30 transition-colors disabled:opacity-30"
            >
              Sblocca ▶
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => { setPin(''); setErr(null); setShowPass(false); setMode((m) => (m === 'pin' ? 'passphrase' : 'pin')); }}
        className="text-[13px] text-ink-dim hover:text-ink mt-3"
      >
        {mode === 'pin' ? '🔑 Sblocca con passphrase' : '🔢 Sblocca con il PIN'}
      </button>

      {err && (
        <Box variant="danger" className="text-[16px] mt-3">
          <span className="text-red-500">✕</span> {err}
        </Box>
      )}

      {busy && (
        <Box variant="warn" className="text-[16px] mt-3">
          decifrando<Cursor />
        </Box>
      )}

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/recover')}>
          Ho dimenticato il PIN
        </Btn>
        <Btn variant="ghost" onClick={() => navigate('/welcome')}>
          ↩ Usa un altro account
        </Btn>
      </Footer>
      <p className="text-[12px] text-ink-dim text-center mt-2">
        creando un nuovo account su questo dispositivo sostituisci quello
        attuale (potrai riaprirlo con le 24 parole).
      </p>

      {/* Splash logo all'apertura: appare ~650ms poi sfuma e rivela il PIN.
          Tap per saltarlo. pointer-events-none a fine così non blocca i tap. */}
      <div
        onClick={() => setIntro(false)}
        className={`fixed inset-0 z-50 bg-black flex flex-col items-center justify-center transition-opacity duration-300 ${intro ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="text-ink text-glow-strong text-[54px] font-bold tracking-[0.22em] leading-none animate-logo-in">
          NULLUM
        </div>
        <div className="text-ink-dim text-[12px] tracking-[0.3em] uppercase mt-4 animate-fade-in">
          messaggi anonimi · cifrati
        </div>
      </div>
    </Frame>
  );
}
